if (!customElements.get('quick-add-opener')) {
    customElements.define(
        'quick-add-opener',
        class QuickAddOpener extends HTMLElement {
            constructor() {
                super();
                const button = this.querySelector('button');
                if (!button) return;

                this.button = button;

                button.addEventListener('click', () => {
                    const modal = this.getModal();
                    if (modal) modal.show(button);
                });

                const preload = () => {
                    const modal = this.getModal();
                    if (modal && typeof modal.preload === 'function') {
                        modal.preload(button.getAttribute('data-product-url'));
                    }
                };

                button.addEventListener('pointerenter', preload, { passive: true });
                button.addEventListener('focus', preload, { passive: true });
            }

            getModal() {
                if (this.modalElement?.isConnected) return this.modalElement;

                const modalSelector = this.getAttribute('data-modal');
                const adjacentModal = this.nextElementSibling;
                if (adjacentModal?.matches?.('quick-add-modal')) {
                    this.modalElement = adjacentModal;
                    return adjacentModal;
                }

                if (!modalSelector) return null;
                const modalId = modalSelector.startsWith('#') ? modalSelector.slice(1) : modalSelector;
                return document.getElementById(modalId);
            }
        }
    );
}

if (!customElements.get('quick-add-modal')) {
    customElements.define(
        'quick-add-modal',
        class QuickAddModal extends HTMLElement {
            constructor() {
                super();
                this.modal = this.querySelector('.quick-add-modal__wrapper');
                this.modalContent = this.querySelector('[id^="QuickAddContent-"]');
                this.overlay = this.querySelector('.quick-add-modal__overlay');
                this.closeButton = this.querySelector('.quick-add-modal__close-btn');
                this.scrollY = 0;
                this.productCache = QuickAddModal.productCache || (QuickAddModal.productCache = new Map());
                this.isOpen = false;
                this.isAnimating = false;
                this.closeTimer = null;
                this.focusTimer = null;
                this.activeRequest = 0;

                this.eventListeners();
            }

            connectedCallback() {
                if (this.moved) return;
                this.moved = true;

                const opener = this.previousElementSibling;
                if (opener?.matches?.('quick-add-opener')) {
                    opener.modalElement = this;
                }

                document.body.appendChild(this);
            }

            getProductHTML(productUrl) {
                if (this.productCache.has(productUrl)) {
                    return Promise.resolve(this.productCache.get(productUrl));
                }

                return fetch(productUrl)
                    .then((response) => response.text())
                    .then((responseText) => {
                        this.productCache.set(productUrl, responseText);
                        return responseText;
                    });
            }

            preload(productUrl) {
                if (!productUrl || this.productCache.has(productUrl)) return;
                this.getProductHTML(productUrl).catch(() => {});
            }

            renderLoadingState() {
                this.modalContent.innerHTML = `
                    <div class="quick-add-modal__skeleton" aria-hidden="true">
                        <div class="quick-add-modal__skeleton-header">
                            <span></span>
                            <div>
                                <span></span>
                                <span></span>
                            </div>
                        </div>
                        <span></span>
                        <span></span>
                        <span></span>
                    </div>
                `;
            }

            open() {
                window.clearTimeout(this.closeTimer);
                window.clearTimeout(this.focusTimer);

                this.isOpen = true;
                this.isAnimating = true;
                this.classList.remove('is-open', 'is-closing');

                document.body.classList.add('quick-add-modal-open', 'overflow-hidden');
                this.lockIOSSafariScroll();

                requestAnimationFrame(() => {
                    this.classList.add('motion-ready');
                    requestAnimationFrame(() => {
                        this.classList.add('is-open');
                    });
                });

                this.focusTimer = window.setTimeout(() => {
                    this.isAnimating = false;
                    trapFocus(this, this.querySelector('[role="dialog"]'));
                }, 900);
            }

            isIOSSafari() {
                return /iP(ad|hone|od)/.test(navigator.userAgent)
                    && /Safari/.test(navigator.userAgent)
                    && !/CriOS|FxiOS|EdgiOS/.test(navigator.userAgent);
            }

            lockIOSSafariScroll() {
                if (window.SkinNoteDrawers) {
                    window.SkinNoteDrawers.lock('quick-add');
                    return;
                }
                if (!this.isIOSSafari()) return;

                this.scrollY = window.scrollY || window.pageYOffset || 0;

                document.body.style.position = 'fixed';
                document.body.style.top = `-${this.scrollY}px`;
                document.body.style.left = '0';
                document.body.style.right = '0';
                document.body.style.width = '100%';
            }

            unlockIOSSafariScroll() {
                if (window.SkinNoteDrawers) {
                    window.SkinNoteDrawers.unlock('quick-add');
                    return;
                }
                if (!this.isIOSSafari()) return;

                const scrollY = this.scrollY || 0;
                const previousScrollBehavior = document.documentElement.style.scrollBehavior;

                document.documentElement.style.scrollBehavior = 'auto';

                document.body.style.position = '';
                document.body.style.top = '';
                document.body.style.left = '';
                document.body.style.right = '';
                document.body.style.width = '';

                window.scrollTo(0, scrollY);

                requestAnimationFrame(() => {
                    document.documentElement.style.scrollBehavior = previousScrollBehavior;
                });
            }

            show(opener) {
                const requestId = ++this.activeRequest;
                window.clearTimeout(this.closeTimer);
                window.clearTimeout(this.focusTimer);
                this.classList.remove('is-open', 'is-closing');

                opener.setAttribute('aria-disabled', true);
                opener.setAttribute('aria-busy', 'true');
                opener.classList.add('loading');
                this.openedBy = opener;

                const productUrl = opener.getAttribute('data-product-url');
                const request = this.getProductHTML(productUrl);
                const shouldShowLoadingState = !this.productCache.has(productUrl);

                if (shouldShowLoadingState) {
                    this.renderLoadingState();
                    this.open();
                }

                request
                    .then((responseText) => {
                        if (requestId !== this.activeRequest) return;

                        const responseHTML = new DOMParser().parseFromString(responseText, 'text/html');
                        this.productElement = responseHTML.querySelector('section[id^="MainProductQuickAdd-"]');
                        
                        if (!this.productElement) {
                            console.warn('Quick add product element not found in response');
                            return;
                        }

                        this.setInnerHTML(this.modalContent, this.productElement.innerHTML);

                        if (window.Shopify && Shopify.PaymentButton) {
                            Shopify.PaymentButton.init();
                        }

                        if (!this.isOpen) this.open();
                    })
                    .catch(error => {
                        console.error('Error loading quick add modal:', error);
                    })
                    .finally(() => {
                        opener.removeAttribute('aria-disabled');
                        opener.removeAttribute('aria-busy');
                        opener.classList.remove('loading');
                    });
            }

            hide() {
                if (!this.isOpen && !this.isAnimating) return;

                this.activeRequest++;
                window.clearTimeout(this.closeTimer);
                window.clearTimeout(this.focusTimer);

                this.isOpen = false;
                this.isAnimating = true;
                this.classList.add('is-closing');
                this.classList.remove('is-open');

                this.closeTimer = window.setTimeout(() => {
                    this.modalContent.innerHTML = '';
                    removeTrapFocus(this.openedBy);
                    document.body.classList.remove('quick-add-modal-open', 'overflow-hidden');
                    this.unlockIOSSafariScroll();
                    this.classList.remove('motion-ready', 'is-closing');
                    this.isAnimating = false;
                    document.body.dispatchEvent(new CustomEvent('modalClosed'));
                }, 840);
            }

            setInnerHTML(element, html) {
                element.innerHTML = html;

                // Reinjects the script tags to allow execution. By default, scripts are disabled when using element.innerHTML.
                element.querySelectorAll('script').forEach((oldScriptTag) => {
                    const newScriptTag = document.createElement('script');
                    Array.from(oldScriptTag.attributes).forEach((attribute) => {
                        newScriptTag.setAttribute(attribute.name, attribute.value);
                    });
                    newScriptTag.appendChild(document.createTextNode(oldScriptTag.innerHTML));
                    oldScriptTag.parentNode.replaceChild(newScriptTag, oldScriptTag);
                });
            }

            eventListeners() {
                this.closeButton.addEventListener('click', this.hide.bind(this))
                this.overlay.addEventListener('click', this.hide.bind(this))
                this.addEventListener('keyup', (event) => {
                    if (event.code.toUpperCase() === 'ESCAPE') this.hide();
                });
            }
        }
    );
}
